import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getEdgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { BANK_LINK_RETURN_KEY } from "@/lib/open-banking";
import { AuthShell, AuthSubmit } from "@/platform/auth/AuthShell";
import { sharedPageProduct } from "@/platform/auth/brand";
import { productForPath } from "@/platform/products";
import { platform } from "@/platform/runtime";

/**
 * Ritorno dalla banca dopo il consenso open banking (/auth/banca?code=…&state=…).
 * Completa il collegamento, avvia la prima importazione dei movimenti e riporta alla pagina
 * da cui era partito. Condivisa da tutti i prodotti.
 */
export default function BankLinkCallback() {
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState("");
  const started = useRef(false);

  const returnPath = (() => {
    try {
      return sessionStorage.getItem(BANK_LINK_RETURN_KEY) || "/";
    } catch {
      return "/";
    }
  })();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const bankError = params.get("error_description") || params.get("error");
    const code = params.get("code");
    const authState = params.get("state");

    const run = async () => {
      if (bankError || !code || !authState) {
        throw new Error(bankError ? `La banca ha risposto: ${bankError}` : "Autorizzazione non completata");
      }
      const done = await supabase.functions.invoke("enable-banking", {
        body: { action: "complete", code, state: authState },
      });
      if (done.error || done.data?.error) throw new Error(await getEdgeFunctionErrorMessage(done.error, done.data));
      // Prima importazione: non blocca il ritorno se la banca è lenta
      await supabase.functions.invoke("enable-banking", { body: { action: "sync" } }).catch(() => undefined);
    };

    run()
      .then(() => {
        setState("done");
        try {
          sessionStorage.removeItem(BANK_LINK_RETURN_KEY);
        } catch {
          /* niente */
        }
        setTimeout(() => window.location.replace(returnPath), 1500);
      })
      .catch((e: unknown) => {
        setState("error");
        setMessage(e instanceof Error ? e.message : "Collegamento non riuscito");
      });
  }, [returnPath]);

  const brandProduct = sharedPageProduct(platform.product, returnPath, productForPath);

  return (
    <AuthShell
      product={brandProduct}
      title={
        state === "working" ? "Collegamento della banca" : state === "done" ? "Banca collegata" : "Collegamento non riuscito"
      }
      subtitle={
        state === "working"
          ? "Stiamo completando il collegamento e importando i movimenti."
          : state === "done"
            ? "Ti riportiamo alla pagina di prima."
            : "Nessun dato è stato importato."
      }
    >
      <div className="flex flex-col items-center gap-5 py-2 text-center">
        {state === "working" && <Loader2 className="h-10 w-10 animate-spin text-slate-400" />}
        {state === "done" && <CheckCircle2 className="h-10 w-10 text-green-600" />}
        {state === "error" && (
          <>
            <XCircle className="h-10 w-10 text-red-600" />
            <p className="text-sm text-slate-600">{message}</p>
            <AuthSubmit type="button" product={brandProduct} onClick={() => window.location.replace(returnPath)}>
              Torna indietro
            </AuthSubmit>
          </>
        )}
      </div>
    </AuthShell>
  );
}
